import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
// Straight at the service rather than through the appointments barrel, for the
// same reason `useDayTasks` does it: the barrel pulls that feature's screens in
// behind one function.
import { hasEverBooked } from "../../appointments/services/appointmentsService";
import { EVER_BOOKED_QUERY_KEY } from "../../appointments/appointmentCaches";

/**
 * Whether this practice has ever booked anything — and whether we know yet.
 *
 * Three answers rather than two, because "we have not heard back" is not
 * "no". An empty day says something different depending on which of the three
 * this is, and a hook that collapsed the first into the last would have a
 * first-run panel appear a beat after the compact one on every single load.
 */
export type EverBooked = "unknown" | "never" | "before";

export interface EverBookedOptions {
  /**
   * Whether the question is worth asking at all. Off by default: a day with
   * rows in it has already answered it, and most days have rows.
   */
  enabled?: boolean;
}

/**
 * Has this practitioner ever booked an appointment?
 *
 * One read, asked only when something turns on the answer, and kept for the
 * session once it has one.
 *
 * It exists for a single decision on My task: an empty day is either the first
 * thing a new workspace sees — in which case it is worth saying what the
 * screen is for — or an ordinary free afternoon in a practice that has been
 * running for a year, in which case the welcome mat is noise over a fact the
 * reader can already see. Only this tells the two apart. The day itself
 * cannot: both look identical from inside one date.
 *
 * `enabled` is how it stays nearly free. The caller asks only once it knows
 * the day is empty, so a practitioner with a full Tuesday never sends the
 * request at all — and a day with rows in it has proved the answer anyway.
 *
 * Nothing ever expires it. A `false` is turned over by the write path rather
 * than by a clock: `invalidateAppointmentReads` writes `true` into this key
 * the moment any appointment is booked, so the first booking of a practice's
 * life settles it without a second round trip. A `true` cannot become false
 * again — an appointment that existed, existed.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useEverBooked({
  enabled = false,
}: EverBookedOptions = {}): EverBooked {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: EVER_BOOKED_QUERY_KEY,
    queryFn: ({ signal }) => hasEverBooked(getToken, signal),
    enabled,
    staleTime: Infinity,
  });

  // A failure answers "before" rather than "unknown", and that is the safe way
  // round. The panel this gates is the loud one, and showing it to a practice
  // that has been booking for a year because a probe timed out is worse than
  // withholding it from a new one — who still has Create on the day strip
  // above, and will see the panel the next time the question is asked.
  if (query.isError) return "before";
  if (query.data === undefined) return "unknown";
  return query.data ? "before" : "never";
}

export default useEverBooked;
